import { useCallback, useContext } from "react";
import { FeedCardAggregatedReactions } from "src/components/feed-card/content-blocks/aggregated-reactions/feed-card-aggregated-reactions";
import { FeedCardCategories } from "src/components/feed-card/content-blocks/categories/feed-card-categories";
import { FeedCardGroup } from "src/components/feed-card/content-blocks/group/feed-card-group";
import { FeedCardHeadline } from "src/components/feed-card/content-blocks/headline/feed-card-headline";
import { FeedCardIconText } from "src/components/feed-card/content-blocks/icon-text/feed-card-icon-text";
import { FeedCardImage } from "src/components/feed-card/content-blocks/image/feed-card-image";
import { FeedCardLink } from "src/components/feed-card/content-blocks/link/feed-card-link";
import { FeedCardNumber } from "src/components/feed-card/content-blocks/number/feed-card-number";
import { FeedCardPrice } from "src/components/feed-card/content-blocks/price/feed-card-price";
import { FeedCardRichText } from "src/components/feed-card/content-blocks/rich-text/feed-card-rich-text";
import { FeedCardSmartParagraph } from "src/components/feed-card/content-blocks/smart-paragraph/feed-card-smart-paragraph";
import { FeedCardStackedBarChart } from "src/components/feed-card/content-blocks/stacked-bar-chart/feed-card-stacked-bar-chart";
import { FeedCardStockLoverNews } from "src/components/feed-card/content-blocks/stock-lover-news/feed-card-stock-lover-news";
import { FeedCardTable } from "src/components/feed-card/content-blocks/table/feed-card-table";
import { FeedCardTag } from "src/components/feed-card/content-blocks/tag/feed-card-tag";
import { FeedCardText } from "src/components/feed-card/content-blocks/text/feed-card-text";
import { FeedCardTradingMotive } from "src/components/feed-card/content-blocks/trading-motive/feed-card-trading-motive";
import { FeedCardUserReaction } from "src/components/feed-card/content-blocks/user-reaction/feed-card-user-reaction";
import { FeedCardVoting } from "src/components/feed-card/content-blocks/voting/feed-card-voting";
import { FeedCardContext } from "src/components/feed-card/feed-card";
import { FeedCardActor } from "src/components/feed-card/feed-card-actor";
import { TFeedCardContentBlock } from "src/components/feed-card/types/content";
import { EnumFeedCardBlockType } from "src/components/feed-card/types/enums";
import { EnumFeedClickTrackingArea, useFeedClickTracking } from "src/components/pages/feed/hooks/use-feed-click-tracking";

export const FeedCardContentBlock = ({ type, body }: TFeedCardContentBlock) => {
    const context = useContext(FeedCardContext);
    const { trackFeedClick } = useFeedClickTracking();

    const handleLinkClick = useCallback(
        (url: string) => {
            trackFeedClick(context.contentId, url, EnumFeedClickTrackingArea.Body);
        },
        [context.contentId, trackFeedClick]
    );

    switch (type) {
        case EnumFeedCardBlockType.Link:
            return <FeedCardLink {...body} onClick={handleLinkClick} />;
        case EnumFeedCardBlockType.Text:
            return <FeedCardText value={body} />;
        case EnumFeedCardBlockType.RichText:
            return <FeedCardRichText {...body} />;
        case EnumFeedCardBlockType.SmartParagraph:
            return <FeedCardSmartParagraph {...body} onLinkClick={handleLinkClick} />;
        case EnumFeedCardBlockType.Price:
            return <FeedCardPrice {...body} />;
        case EnumFeedCardBlockType.Number:
            return <FeedCardNumber {...body} />;
        case EnumFeedCardBlockType.Table:
            return <FeedCardTable {...body} />;
        case EnumFeedCardBlockType.Image:
            return <FeedCardImage {...body} />;
        case EnumFeedCardBlockType.Tag:
            return <FeedCardTag {...body} />;
        case EnumFeedCardBlockType.StackedBarChart:
            return <FeedCardStackedBarChart items={body} />;
        case EnumFeedCardBlockType.IconText:
            return <FeedCardIconText {...body} />;
        case EnumFeedCardBlockType.Group:
            return <FeedCardGroup content={body} />;
        case EnumFeedCardBlockType.Headline:
            return <FeedCardHeadline {...body} />;
        case EnumFeedCardBlockType.Categories:
            return <FeedCardCategories categories={body} />;
        case EnumFeedCardBlockType.Voting:
            return <FeedCardVoting {...body} />;
        case EnumFeedCardBlockType.Actor:
            return <FeedCardActor {...body} />;
        case EnumFeedCardBlockType.AggregatedReactionsBar:
            return <FeedCardAggregatedReactions {...body} />;
        case EnumFeedCardBlockType.UserReaction:
            return <FeedCardUserReaction {...body} />;
        case EnumFeedCardBlockType.StockLoverNews:
            return <FeedCardStockLoverNews {...body} />;
        case EnumFeedCardBlockType.TradingMotive:
            return <FeedCardTradingMotive {...body} />;
        default:
            return <UnsupportedContentType type={type} />;
    }
};

const UnsupportedContentType = ({}: { type: never }) => null;
